import React, { useState, useEffect, useRef } from 'react';
import { BookOpenIcon, MagnifyingGlassIcon, SparklesIcon, LockClosedIcon } from '@heroicons/react/24/solid';

/**
 * PrivateRAG component showcasing Raidu's private retrieval-augmented generation capabilities
 * Displays the key features and benefits of Raidu's private RAG technology
 * 
 * @returns {JSX.Element} The rendered PrivateRAG component
 */
const PrivateRAG: React.FC = () => {
  try {
    // Log component rendering
    console.log('Rendering PrivateRAG component');
    
    // State for chat messages and animation
    const [messages, setMessages] = useState<Array<{type: string, text: string, citation?: string, agent?: string, sources?: Array<{id: string, type: string, name: string}>}>>([]);
    const [isTyping, setIsTyping] = useState(false);
    const [currentMessage, setCurrentMessage] = useState("");
    const [inputValue, setInputValue] = useState("");
    const [demoStarted, setDemoStarted] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<string[]>([]);
    const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
    const [animationTimers, setAnimationTimers] = useState<NodeJS.Timeout[]>([]);
    
    // AI provider icons to rotate through
    const aiProviderIcons = [
      { name: "OpenAI", icon: "/ai-icons/openai.svg" },
      { name: "Anthropic", icon: "/ai-icons/anthropic.svg" },
      { name: "Gemini", icon: "/ai-icons/gemini-color.svg" },
      { name: "Mistral", icon: "/ai-icons/mistral-color.svg" },
      { name: "Cohere", icon: "/ai-icons/cohere-color.svg" },
      { name: "Meta", icon: "/ai-icons/meta-color.svg" }
    ];
    
    // Agent types for the demo
    const agentTypes = [
      { id: "retriever", name: "Retrieval Agent", icon: <BookOpenIcon className="h-4 w-4" />, color: "bg-blue-600" },
      { id: "analyzer", name: "Analysis Agent", icon: <MagnifyingGlassIcon className="h-4 w-4" />, color: "bg-green-600" },
      { id: "generator", name: "Generation Agent", icon: <SparklesIcon className="h-4 w-4" />, color: "bg-purple-600" },
      { id: "security", name: "Security Agent", icon: <LockClosedIcon className="h-4 w-4" />, color: "bg-red-600" }
    ];
    
    // Current icon index
    const [currentIconIndex, setCurrentIconIndex] = useState(0);
    
    // Reference to chat container for auto-scrolling
    const chatContainerRef = useRef<HTMLDivElement>(null);
    
    // Data sources for the demo - specific platforms without category separation
    const dataSources = [
      // Google Drive
      { id: "gdrive1", type: "gdrive", title: "Q2 Financial Report (Google Drive)", content: "Spreadsheet with quarterly financial data, revenue projections, and growth metrics." },
      { id: "gdrive2", type: "gdrive", title: "Marketing Strategy 2024 (Google Drive)", content: "Document outlining marketing initiatives, target demographics, and campaign timelines." },
      
      // GitHub
      { id: "github1", type: "github", title: "Backend API Repository (GitHub)", content: "Source code for the company's core API services with documentation." },
      { id: "github2", type: "github", title: "Frontend App Repository (GitHub)", content: "React component library and application code with test coverage reports." },
      
      // MongoDB
      { id: "mongo1", type: "mongodb", title: "User Analytics (MongoDB)", content: "Database containing user behavior data, feature usage, and interaction patterns." },
      { id: "mongo2", type: "mongodb", title: "Product Catalog (MongoDB)", content: "Database of product information, pricing tiers, and feature availability." },
      
      // QuickBooks
      { id: "qb1", type: "quickbooks", title: "Annual Financial Records (QuickBooks)", content: "Accounting data including revenue, expenses, and tax information." },
      { id: "qb2", type: "quickbooks", title: "Quarterly Tax Reports (QuickBooks)", content: "Tax filing data and payment records for quarterly business taxes." },
      
      // Salesforce
      { id: "sf1", type: "salesforce", title: "Customer Accounts (Salesforce)", content: "CRM data on customer accounts, contacts, and engagement history." },
      { id: "sf2", type: "salesforce", title: "Sales Pipeline (Salesforce)", content: "Deal tracking data with opportunity stages, values, and forecasts." },
      
      // Confluence
      { id: "conf1", type: "confluence", title: "Engineering Wiki (Confluence)", content: "Technical documentation, architecture diagrams, and development guidelines." },
      { id: "conf2", type: "confluence", title: "Product Requirements (Confluence)", content: "Detailed product specifications, user stories, and feature requirements." },
      
      // Slack
      { id: "slack1", type: "slack", title: "Product Team Discussions (Slack)", content: "Conversation history from product team channels about feature development." },
      { id: "slack2", type: "slack", title: "Customer Support Threads (Slack)", content: "Support conversations and issue resolution discussions from support channels." }
    ];
    
    // Sample Q&A pairs for the demo with agent workflows
    const sampleQA = [
      { 
        question: "What were our financial results last quarter?", 
        agentFlow: [
          { agent: "retriever", message: "Searching for financial data from Q2 2024..." },
          { agent: "analyzer", message: "Analyzing financial reports and extracting key metrics..." },
          { agent: "generator", message: "Generating response based on financial data..." }
        ],
        answer: "According to our Q2 Financial Report, the company's revenue increased by 15% in Q2 2024, with the new AI product line contributing significantly to this growth.",
        sources: ["gdrive1", "qb1"],
        searchTerms: ["financial", "revenue", "Q2", "report"]
      },
      { 
        question: "What's on our product roadmap for this year?", 
        agentFlow: [
          { agent: "retriever", message: "Retrieving product roadmap documents..." },
          { agent: "analyzer", message: "Analyzing roadmap timeline and key milestones..." },
          { agent: "generator", message: "Generating response based on roadmap analysis..." }
        ],
        answer: "Based on our Product Roadmap 2024-2025, our AI platform will expand to include advanced RAG capabilities and multi-modal processing by Q3 2024.",
        sources: ["gdrive2", "conf2"],
        searchTerms: ["roadmap", "product", "AI", "capabilities"]
      },
      { 
        question: "How do we handle sensitive data?", 
        agentFlow: [
          { agent: "security", message: "Verifying user permissions for sensitive data access..." },
          { agent: "retriever", message: "Retrieving security protocols and compliance documents..." },
          { agent: "analyzer", message: "Analyzing security requirements and data handling procedures..." },
          { agent: "generator", message: "Generating response with security protocol information..." }
        ],
        answer: "According to our Internal Security Protocol, all sensitive data must be encrypted using AES-256 and processed only within our secure cloud environment.",
        sources: ["github1", "conf1"],
        searchTerms: ["security", "sensitive", "data", "encrypted"]
      },
      { 
        question: "What feedback have we received from customers?", 
        agentFlow: [
          { agent: "retriever", message: "Retrieving customer feedback data..." },
          { agent: "analyzer", message: "Analyzing customer sentiment and common requests..." },
          { agent: "generator", message: "Generating summary of customer feedback..." }
        ],
        answer: "According to our Customer Feedback Summary and CRM data, customers have requested improved document processing speeds and better integration with existing knowledge management systems.",
        sources: ["sf1", "slack2", "mongo1"],
        searchTerms: ["customer", "feedback", "requests"]
      }
    ];
    
    // Function to clear all animation timers
    const clearAllTimers = () => {
      try {
        // Clear all stored timers
        animationTimers.forEach(timer => clearTimeout(timer));
        setAnimationTimers([]);
      } catch (error) {
        console.error("Error clearing animation timers:", error);
      }
    };
    
    // Function to auto-scroll chat to bottom
    const scrollToBottom = () => {
      try {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      } catch (error) {
        console.error("Error scrolling chat to bottom:", error);
      }
    };
    
    // Effect to scroll chat to bottom when messages change
    useEffect(() => {
      scrollToBottom();
    }, [messages, currentMessage]);
    
    // Function to simulate typing animation
    const simulateTyping = (text: string, callback?: () => void) => {
      try {
        setIsTyping(true);
        setCurrentMessage("");
        
        let index = 0;
        const typingSpeed = 30; // ms per character
        
        const typingInterval = setInterval(() => {
          if (index < text.length) {
            setCurrentMessage(prev => prev + text.charAt(index));
            index++;
          } else {
            clearInterval(typingInterval);
            setIsTyping(false);
            setCurrentMessage("");
            if (callback) callback();
          }
        }, typingSpeed);
        
        // Store the interval for cleanup
        setAnimationTimers(prev => [...prev, typingInterval]);
        
        return typingInterval;
      } catch (error) {
        console.error("Error simulating typing:", error);
        setIsTyping(false);
        return null;
      }
    };
    
    // Function to simulate data source search
    const simulateDataSourceSearch = (_searchTerms: string[], sources: string[], callback?: () => void) => {
      try {
        setIsSearching(true);
        setSearchResults([]);
        
        // First show searching animation
        const searchingTimer = setTimeout(() => {
          // Then show results one by one
          let sourceIndex = 0;
          
          const showResultsInterval = setInterval(() => {
            if (sourceIndex < sources.length) {
              const sourceId = sources[sourceIndex];
              const source = dataSources.find(d => d.id === sourceId);
              
              if (source) {
                setSearchResults(prev => [...prev, source.title]);
              }
              
              sourceIndex++;
            } else {
              clearInterval(showResultsInterval);
              
              // Wait a bit before selecting the most relevant source
              const selectSourceTimer = setTimeout(() => {
                if (sources.length > 0) {
                  setSelectedDocument(sources[0]);
                }
                
                setIsSearching(false);
                
                if (callback) {
                  callback();
                }
              }, 800);
              
              setAnimationTimers(prev => [...prev, selectSourceTimer]);
            }
          }, 400);
          
          setAnimationTimers(prev => [...prev, showResultsInterval]);
        }, 1500);
        
        setAnimationTimers(prev => [...prev, searchingTimer]);
      } catch (error) {
        console.error("Error simulating data source search:", error);
        setIsSearching(false);
      }
    };
    
    // Function to handle sending a message
    const handleSendMessage = (message: string = "") => {
      try {
        if (demoStarted) return;
        
        const userMessage = message || inputValue;
        if (!userMessage.trim()) return;
        
        setDemoStarted(true);
        setInputValue("");
        
        // Add user message to chat
        setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
        
        // Find a matching sample Q&A or use the first one as default
        const qaMatch = sampleQA.find(qa => 
          qa.question.toLowerCase() === userMessage.toLowerCase() ||
          qa.question.toLowerCase().includes(userMessage.toLowerCase()) ||
          userMessage.toLowerCase().includes(qa.question.toLowerCase())
        ) || sampleQA[0];
        
        // Process agent flow if available
        if (qaMatch.agentFlow && qaMatch.agentFlow.length > 0) {
          // Create a map to track unique agent types to prevent duplicates
          const processedAgents = new Map();
          let stepIndex = 0;
          
          const processNextStep = () => {
            if (stepIndex < qaMatch.agentFlow.length) {
              const step = qaMatch.agentFlow[stepIndex];
              
              // Only add this agent message if we haven't seen this agent type before
              // or if it's a different message from the same agent
              const agentKey = `${step.agent}-${step.message}`;
              if (!processedAgents.has(agentKey)) {
                processedAgents.set(agentKey, true);
                
                // Add agent message to chat
                setMessages(prev => [...prev, { 
                  type: 'agent', 
                  text: step.message,
                  agent: step.agent
                }]);
              }
              
              stepIndex++;
              
              // Process next step after a delay
              setTimeout(processNextStep, 1500);
            } else {
              // After all agent steps, simulate data source search
              simulateDataSourceSearch(qaMatch.searchTerms, qaMatch.sources, () => {
                // After search completes, simulate AI typing the response
                simulateTyping(qaMatch.answer, () => {
                  // Get source information
                  const sourcesInfo = qaMatch.sources.map(sourceId => {
                    const source = dataSources.find(d => d.id === sourceId);
                    return source ? { id: source.id, type: source.type, name: source.title } : undefined;
                  }).filter((item): item is {id: string, type: string, name: string} => item !== undefined);
                  
                  // Add AI response to chat after typing animation
                  setMessages(prev => [...prev, { 
                    type: 'ai', 
                    text: qaMatch.answer,
                    citation: sourcesInfo.length > 0 ? sourcesInfo[0].name : undefined,
                    sources: sourcesInfo
                  }]);
                  
                  // Reset for next interaction
                  setTimeout(() => {
                    setDemoStarted(false);
                    setSelectedDocument(null);
                    setSearchResults([]);
                  }, 1000);
                });
              });
            }
          };
          
          // Start processing agent flow
          processNextStep();
        } else {
          // Fallback to simple search if no agent flow defined
          simulateDataSourceSearch(qaMatch.searchTerms, qaMatch.sources || [], () => {
            simulateTyping(qaMatch.answer, () => {
              // Get source information
              const sourcesInfo = (qaMatch.sources || []).map(sourceId => {
                const source = dataSources.find(d => d.id === sourceId);
                return source ? { id: source.id, type: source.type, name: source.title } : undefined;
              }).filter((item): item is {id: string, type: string, name: string} => item !== undefined);
              
              setMessages(prev => [...prev, { 
                type: 'ai', 
                text: qaMatch.answer,
                citation: sourcesInfo.length > 0 ? sourcesInfo[0].name : undefined,
                sources: sourcesInfo
              }]);
              
              setTimeout(() => {
                setDemoStarted(false);
                setSelectedDocument(null);
                setSearchResults([]);
              }, 1000);
            });
          });
        }
        
        // Log the interaction
        console.log("User message:", userMessage);
      } catch (error) {
        console.error("Error handling send message:", error);
        setDemoStarted(false);
      }
    };
    
    // Function to reset the demo
    const resetDemo = () => {
      try {
        // Log reset action
        console.log("Resetting PrivateRAG demo");
        
        // Clear all animation timers
        clearAllTimers();
        
        // Reset all state
        setMessages([]);
        setIsTyping(false);
        setCurrentMessage("");
        setInputValue("");
        setDemoStarted(false);
        setIsSearching(false);
        setSearchResults([]);
        setSelectedDocument(null);
        
        // Start demo with a suggested question after a short delay
        const startDemoTimer = setTimeout(() => {
          handleSendMessage(sampleQA[0].question);
        }, 1000);
        
        setAnimationTimers([startDemoTimer]);
      } catch (error) {
        console.error("Error resetting demo:", error);
      }
    };
    
    // Set up icon rotation and start demo on component mount
    useEffect(() => {
      try {
        // Set up icon rotation
        const iconRotationTimer = setInterval(() => {
          try {
            setCurrentIconIndex(prevIndex => (prevIndex + 1) % aiProviderIcons.length);
          } catch (error) {
            console.error("Error rotating AI provider icon:", error);
          }
        }, 3000); // Rotate every 3 seconds
        
        // Start demo with initial question after a short delay
        const startDemoTimer = setTimeout(() => {
          handleSendMessage(sampleQA[0].question);
        }, 1000);
        
        setAnimationTimers([iconRotationTimer, startDemoTimer]);
        
        return () => {
          // Clean up all timers when component unmounts
          clearAllTimers();
          clearInterval(iconRotationTimer);
        };
      } catch (error) {
        console.error("Error setting up PrivateRAG demo:", error);
      }
    }, []);

    return (
      <div id="private-rag" className="relative overflow-hidden">
        {/* Decorative elements matching Hero section */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-indigo-200 blur-3xl"></div>
          <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-purple-200 blur-3xl"></div>
          <div className="absolute -bottom-24 left-1/4 w-72 h-72 rounded-full bg-pink-200 blur-3xl"></div>
        </div>
        
        {/* Subtle dot pattern overlay */}
        <div 
          className="absolute inset-0 opacity-10" 
          style={{
            backgroundImage: 'radial-gradient(#4f46e5 0.5px, transparent 0.5px)',
            backgroundSize: '12px 12px'
          }}
        ></div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-indigo-200/30 to-purple-200/30 rounded-full blur-3xl -z-10 transform translate-x-1/2 -translate-y-1/4"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-br from-blue-200/30 to-indigo-200/30 rounded-full blur-3xl -z-10 transform -translate-x-1/2 translate-y-1/4"></div>
        
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 z-10">
          <div className="grid grid-cols-1 items-center gap-x-8 gap-y-16 lg:grid-cols-2 p-8 sm:p-12 lg:p-16">
            <div className="mx-auto w-full max-w-xl lg:mx-0">
              <h2 className="text-base/7 font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent inline-block">Enterprise-Grade Security</h2>
              <p className="mt-2 max-w-lg text-4xl font-semibold tracking-tight bg-gradient-to-r from-indigo-700 via-purple-700 to-blue-700 bg-clip-text text-transparent sm:text-5xl">
                Private Retrieval-Augmented Generation
              </p>
              <p className="mt-6 text-lg text-gray-600">
                Raidu's private RAG technology enables AI to securely access your enterprise knowledge without exposing sensitive data. Our system retrieves relevant information from your documents while maintaining complete privacy and compliance.
              </p>
              <div className="mt-8 flex items-center gap-x-6">
                <a
                  href="/booking"
                  className="rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-md hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => {
                    try {
                      // Log button click
                      console.log("PrivateRAG - Get Started button clicked");
                    } catch (error) {
                      // Log any errors during click handling
                      console.error(
                        "Error handling Get Started button click:",
                        error
                      );
                    }
                  }}
                >
                  Get Started
                </a>
                <a
                  href="/booking"
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 transition-colors"
                  onClick={() => {
                    try {
                      // Log button click
                      console.log("PrivateRAG - Learn More button clicked");
                    } catch (error) {
                      // Log any errors during click handling
                      console.error(
                        "Error handling Learn More button click:",
                        error
                      );
                    }
                  }}
                >
                  Learn More <span aria-hidden="true">&rarr;</span>
                </a>
              </div>
            </div>
            
            {/* Interactive RAG Demo */}
            <div className="relative mx-auto w-full max-w-xl lg:mx-0 lg:max-w-none lg:pl-8">
              <div className="rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                {/* Demo header */}
                <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                    <div className="flex items-center ml-2">
                      <img src="/logos/raidu_logo.png" alt="Raidu" className="h-5 w-5 mr-2" />
                      <span className="text-gray-800 text-sm font-medium">Raidu Private RAG</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div 
                      onClick={resetDemo}
                      className="relative flex cursor-pointer transition-transform hover:scale-110"
                      title={`${aiProviderIcons[currentIconIndex].name} (Reset Demo)`}
                    >
                      {/* Rotating AI provider icon */}
                      <img 
                        src={aiProviderIcons[currentIconIndex].icon} 
                        alt={aiProviderIcons[currentIconIndex].name} 
                        className="h-7 w-7 z-10 transition-opacity duration-300" 
                        key={aiProviderIcons[currentIconIndex].name}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Demo content area */}
                <div className="flex flex-col h-[500px]">
                  {/* Left side: Document search and results */}
                  <div className="flex h-full">
                    <div className="w-1/3 border-r border-gray-200 bg-gray-50 p-4 flex flex-col">
                      <div className="text-sm font-medium text-gray-700 mb-2">Connected Data Sources</div>
                      
                      {/* Search status */}
                      {isSearching && (
                        <div className="flex items-center text-xs text-indigo-600 mb-3">
                          <svg className="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Searching data sources...
                        </div>
                      )}
                      
                      {/* Data sources list without category grouping */}
                      <div className="flex-1 overflow-y-auto">
                        {dataSources.map(source => (
                          <div 
                            key={source.id} 
                            className={`p-2 mb-2 rounded text-xs ${searchResults.includes(source.title) 
                              ? selectedDocument === source.id 
                                ? 'bg-indigo-100 border border-indigo-300' 
                                : 'bg-gray-100 border border-gray-200' 
                              : 'bg-white border border-gray-200'}`}
                          >
                            <div className="font-medium flex items-center">
                              <span className="mr-2 flex-shrink-0">
                                {source.type === 'gdrive' && <img src="/data-sources/gdrive.png" alt="Google Drive" className="h-5 w-5 object-contain" />}
                                {source.type === 'github' && <img src="/data-sources/github.png" alt="GitHub" className="h-5 w-5 object-contain" />}
                                {source.type === 'mongodb' && <img src="/data-sources/database.png" alt="MongoDB" className="h-5 w-5 object-contain" />}
                                {source.type === 'quickbooks' && <img src="/data-sources/quickbooks.png" alt="QuickBooks" className="h-5 w-5 object-contain" />}
                                {source.type === 'salesforce' && <img src="/data-sources/api.png" alt="Salesforce" className="h-5 w-5 object-contain" />}
                                {source.type === 'confluence' && <img src="/data-sources/folder.png" alt="Confluence" className="h-5 w-5 object-contain" />}
                                {source.type === 'slack' && <img src="/data-sources/api.png" alt="Slack" className="h-5 w-5 object-contain" />}
                              </span>
                              <span className="truncate">{source.title}</span>
                            </div>
                            <div className="text-gray-500 mt-1 text-[10px] truncate">{source.content}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Right side: Chat interface */}
                    <div className="w-2/3 flex flex-col">
                      {/* Chat messages */}
                      <div 
                        ref={chatContainerRef}
                        className="flex-1 overflow-y-auto p-4 bg-white" 
                        id="rag-chat-container"
                      >
                        {messages.map((message, index) => (
                          <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} mb-4`}>
                            {message.type !== 'user' && (
                              <div className="flex-shrink-0 mr-2">
                                {message.type === 'agent' ? (
                                  <div className={`h-8 w-8 rounded-full ${agentTypes.find(a => a.id === message.agent)?.color || 'bg-gray-600'} flex items-center justify-center text-white text-sm font-bold`}>
                                    {agentTypes.find(a => a.id === message.agent)?.icon || <SparklesIcon className="h-4 w-4" />}
                                  </div>
                                ) : (
                                  <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                                    AI
                                  </div>
                                )}
                              </div>
                            )}
                            <div 
                              className={`rounded-lg px-4 py-2 ${message.type === 'user' 
                                ? 'bg-indigo-600 text-white max-w-[70%]' 
                                : message.type === 'agent'
                                  ? 'bg-gray-100 text-gray-800 max-w-[70%] border border-gray-200'
                                  : 'bg-gray-100 text-gray-800 max-w-[70%]'}`}
                              style={{ wordBreak: 'break-word' }}
                            >
                              {message.type === 'agent' && (
                                <div className="text-xs font-semibold mb-1 text-gray-600">
                                  {agentTypes.find(a => a.id === message.agent)?.name || 'Agent'}
                                </div>
                              )}
                              <p className="text-sm">{message.text}</p>
                              {message.citation && (
                                <div className="mt-1 text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded inline-block">
                                  Source: {message.citation}
                                </div>
                              )}
                              {message.sources && message.sources.length > 0 && (
                                <div className="mt-2 text-xs">
                                  <div className="font-medium text-gray-600 mb-1">Data Sources:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {message.sources.map((source, idx) => (
                                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-[10px] flex items-center">
                                        <span className="mr-1 flex-shrink-0">
                                          {source.type === 'gdrive' && <img src="/data-sources/gdrive.png" alt="Google Drive" className="h-4 w-4 object-contain" />}
                                          {source.type === 'github' && <img src="/data-sources/github.png" alt="GitHub" className="h-4 w-4 object-contain" />}
                                          {source.type === 'mongodb' && <img src="/data-sources/database.png" alt="MongoDB" className="h-4 w-4 object-contain" />}
                                          {source.type === 'quickbooks' && <img src="/data-sources/quickbooks.png" alt="QuickBooks" className="h-4 w-4 object-contain" />}
                                          {source.type === 'salesforce' && <img src="/data-sources/api.png" alt="Salesforce" className="h-4 w-4 object-contain" />}
                                          {source.type === 'confluence' && <img src="/data-sources/folder.png" alt="Confluence" className="h-4 w-4 object-contain" />}
                                          {source.type === 'slack' && <img src="/data-sources/api.png" alt="Slack" className="h-4 w-4 object-contain" />}
                                        </span>
                                        <span className="ml-1">{source.name}</span>
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            {message.type === 'user' && (
                              <div className="flex-shrink-0 ml-2">
                                <div className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-bold">
                                  U
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {/* Typing indicator */}
                        {isTyping && (
                          <div className="flex justify-start mb-4">
                            <div className="flex-shrink-0 mr-2">
                              <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                                AI
                              </div>
                            </div>
                            <div className="rounded-lg px-4 py-2 bg-gray-100 text-gray-800 max-w-[70%]">
                              <p className="text-sm">{currentMessage}<span className="inline-block w-2 h-4 bg-gray-400 ml-1 animate-pulse"></span></p>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Chat input */}
                      <div className="border-t border-gray-200 p-3 bg-white">
                        <form 
                          className="relative" 
                          onSubmit={(e) => {
                            e.preventDefault();
                            if (!demoStarted && inputValue.trim()) {
                              handleSendMessage();
                            }
                          }}
                        >
                          <input 
                            type="text" 
                            className="w-full rounded-full border border-gray-300 px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-ellipsis overflow-hidden"
                            placeholder="Ask about your enterprise data..."
                            value={inputValue}
                            onChange={(e) => {
                              if (!demoStarted) {
                                setInputValue(e.target.value);
                              }
                            }}
                            disabled={demoStarted}
                          />
                          <button 
                            type="submit"
                            className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full ${demoStarted ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:bg-indigo-50 cursor-pointer'}`}
                            disabled={demoStarted}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </form>
                        
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

  } catch (error) {
    // Log any errors during rendering
    console.error('Error rendering PrivateRAG component:', error);
    
    // Return error state
    return (
      <div id="private-rag" className="relative overflow-hidden">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 z-10">
          <div className="text-center p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-indigo-100">
            <p className="text-base font-semibold text-indigo-600">Error</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Failed to load Private RAG section
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Please refresh the page or try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }
};

export default PrivateRAG;
